import { PassengerType, SSR_OPTIONS, SSROptionsKeys } from "src/Modules/Flights/constants/JourneyDetails";

type SSRItem = {
  code: string;
  currency: string;
  price: number;
  quantity?: number;
  weight?: number;
  airlineDescription?: string | null;
};

type SSRColumn = {
  key: string;
  title: string;
  segmentIndex: number;
  flightIndex?: number;
};

type SSRRow = {
  key: number;
  passenger: PassengerType;
  typeIndex: number;
};

type SSRSelectionLayoutProps = {
  rows: SSRRow[];
  columns: SSRColumn[];
  type: SSROptionsKeys;
  formatPrice: (value: number) => string;
  totalLabel: string;
  grandTotal: number;
};

const getItemLabel = (item: SSRItem, type: SSROptionsKeys) => {
  if (type === "baggages") return `${item.weight} kg`;
  if (type === "meals") return item.airlineDescription ?? item.code;
  return item.code;
};

const getSSRColumns =(passenger: PassengerType | undefined, selectionType?: "flight" | "segment"): SSRColumn[] =>
  passenger?.segments?.flatMap((segment, segmentIndex) =>
    selectionType === "segment"
      ? [{ key: `${segmentIndex}`, title: `${segment.segment.origin} → ${segment.segment.destination}`, segmentIndex }]
      : segment.flights.map(({ flight }, flightIndex) => ({
          key: `${segmentIndex}-${flightIndex}`,
          title: `${flight.origin.code} → ${flight.destination.code}`,
          segmentIndex,
          flightIndex,
        })),
  ) ?? [];

const getSSRItems = (passenger: PassengerType, column: SSRColumn, type: SSROptionsKeys) => {
  const segment = passenger.segments?.[column.segmentIndex];
  const ssr = column.flightIndex === undefined ? segment?.ssr : segment?.flights?.[column.flightIndex]?.ssr;
  return Object.values(ssr?.[type] ?? {}) as SSRItem[];
};

const getItemPrice = (item: SSRItem) => (item.price ?? 0) * (item.quantity ?? 1);

const getRowTotal = (passenger: PassengerType, columns: SSRColumn[], type: SSROptionsKeys) =>
  columns.reduce((acc, column) => acc + getSSRItems(passenger, column, type).reduce((sum, item) => sum + getItemPrice(item), 0), 0);

const hasSSR =(passengers: PassengerType[], type?: SSROptionsKeys) => {
  const ssrOption = SSR_OPTIONS.find((option) => option.value === type);
  if (!type || !ssrOption) return false;
  const columns = getSSRColumns(passengers[0], ssrOption.ssrSelectionType);
  return passengers.some((passenger) => columns.some((column) => getSSRItems(passenger, column, type).length > 0));
};

export type { SSRItem, SSRColumn, SSRRow, SSRSelectionLayoutProps };
export { getSSRColumns, getSSRItems, getItemPrice, getItemLabel, getRowTotal, hasSSR };
